import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Heart, ChatCircle, Trash } from "@phosphor-icons/react";
import PhotoAttacher, { AttachedMedia } from "@/components/vansha/PhotoAttacher";

export default function FamilyFeed() {
  const [posts, setPosts] = useState([]);
  const [body, setBody] = useState("");
  const [memoryIds, setMemoryIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");

  const load = async () => { const r = await api.get("/family/feed"); setPosts(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && memoryIds.length === 0) return;
    setBusy(true);
    try {
      await api.post("/family/feed", { body, kind: memoryIds.length ? "photo" : "update", memory_ids: memoryIds });
      setBody(""); setMemoryIds([]);
      load();
    } catch { toast.error("Could not post."); }
    finally { setBusy(false); }
  };

  const react = async (id) => { await api.post(`/family/feed/${id}/react`); load(); };
  const del = async (id) => { await api.delete(`/family/feed/${id}`); load(); };
  const openCom = async (id) => {
    setOpenComments(id);
    const r = await api.get(`/family/feed/${id}/comments`);
    setComments(r.data);
  };
  const sendComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim() || !openComments) return;
    await api.post(`/family/feed/${openComments}/comments`, { body: commentBody });
    setCommentBody("");
    const r = await api.get(`/family/feed/${openComments}/comments`);
    setComments(r.data);
    load();
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="family-feed-page">
      <header>
        <p className="overline text-saffron mb-2">Family feed</p>
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Together</h1>
        <p className="text-ink-700 mt-2">Updates, photos, small celebrations — shared just within the circle.</p>
      </header>

      <form onSubmit={submit} className="vansha-card p-5" data-testid="feed-composer">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} className="input-line font-serif text-lg" rows="3" placeholder="What's happening?" data-testid="feed-body" />
        <div className="mt-3">
          <PhotoAttacher value={memoryIds} onChange={setMemoryIds} accept="image/*,video/*" label="Add photos or videos" />
        </div>
        <div className="flex justify-end mt-3">
          <button disabled={busy || (!body.trim() && memoryIds.length === 0)} className="btn-primary" data-testid="feed-post">Share</button>
        </div>
      </form>

      {posts.length === 0 ? (
        <p className="text-ink-500" data-testid="feed-empty">The feed is quiet. Share something with your family.</p>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <div key={p.id} className="vansha-card p-5" data-testid={`post-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-serif text-lg">{p.author_name}</p>
                  <p className="overline">{new Date(p.created_at).toLocaleString()}</p>
                </div>
                <button onClick={() => del(p.id)} className="btn-ghost p-1" data-testid={`del-${p.id}`}><Trash weight="thin" size={14} /></button>
              </div>
              <p className="whitespace-pre-wrap text-ink-900">{p.body}</p>
              <AttachedMedia ids={p.memory_ids} />
              <div className="mt-4 flex gap-4 text-sm">
                <button onClick={() => react(p.id)} className={`flex items-center gap-1 ${p.reacted ? "text-terracotta" : "text-ink-500"}`} data-testid={`react-${p.id}`}>
                  <Heart weight={p.reacted ? "fill" : "thin"} size={18} /> {p.reactions_count}
                </button>
                <button onClick={() => openCom(p.id)} className="flex items-center gap-1 text-ink-500" data-testid={`open-comments-${p.id}`}>
                  <ChatCircle weight="thin" size={18} /> {p.comments_count}
                </button>
              </div>
              {openComments === p.id && (
                <div className="mt-4 pt-4 border-t border-ink-10 space-y-3" data-testid={`comments-${p.id}`}>
                  {comments.map((c) => (
                    <div key={c.id} className="text-sm" data-testid={`comment-${c.id}`}>
                      <span className="font-medium">{c.user_name}</span>: {c.body}
                    </div>
                  ))}
                  <form onSubmit={sendComment} className="flex gap-2">
                    <input value={commentBody} onChange={(e) => setCommentBody(e.target.value)} className="input-line flex-1" placeholder="Add a comment…" data-testid="comment-input" />
                    <button className="btn-secondary text-sm" data-testid="comment-submit">Send</button>
                  </form>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
